import { Injectable } from '@angular/core';

import {BehaviorSubject, Observable, Subscription, combineLatest, of} from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { ProductListService } from '@shared/services/product-list.service';
import { BayType } from '@shared/models/bay-type';
import { SortActivity } from '@shared/models/sort-activity';
import { Product, ProductSubinventorySummary, ProductSubinventorySummaryProductAssignment} from '@shared/models/product';

import { ShiftContextService } from './shift-context.service';

@Injectable({
    providedIn: 'root'
})
export class ProductService {
    private _currentProducts: BehaviorSubject<ProductSubinventorySummary>;
    subscriptions: Subscription[];
    siteId: string;

    constructor(
            private productListService: ProductListService,
            private shiftContextService: ShiftContextService) {

        this._currentProducts = new BehaviorSubject<ProductSubinventorySummary>(null);

        this.subscriptions = [
            this.shiftContextService.CurrentContext.subscribe(context => {
                if (!context) {
                    return;
                }
                this.siteId = context.site.id;
                this.productListService.getProducts(this.siteId).subscribe(products => {
                    this._currentProducts.next(products);
                });
            }),
        ];
    }

    get CurrentProducts(): Observable<Product[]> {
        return this._currentProducts.pipe(
            filter(summary => summary !== null),
            map(summary => summary.products
                .filter(p => p.isActive)
                .sort((a, z) => a.label.localeCompare(z.label))));
    }

    get AllProducts(): Observable<Product[]> {
        return this._currentProducts.pipe(
            filter(summary => summary !== null),
            map(summary => summary.products)
        );
    }

    FindProduct(id: number): Observable<Product | null | undefined> {
        return this._currentProducts.pipe(map(p => p.products.find(p => p.id === id)));
    }

    private _filterProductsForSubinventory(products: Product[], subinventoryId: number, assignments: ProductSubinventorySummaryProductAssignment[]): Product[] {
        return assignments
            .filter(a => a.siteSubinventoryId === subinventoryId)
            .map(a => products.find(p => p.id === a.productId))
            .filter(p => p?.isActive ?? false)
            .sort((a, z) => a.label.localeCompare(z.label));
    }

    ProductsForSubinventory(siteSubinventoryObs: Observable<number | null>): Observable<Product[]> {
        return combineLatest([this._currentProducts, siteSubinventoryObs]).pipe(
            filter(([summary, _siteSubinventoryId]) => summary !== null),
            map(([summary, siteSubinventoryId]) => {
                return this._filterProductsForSubinventory(summary.products, siteSubinventoryId, summary.subinventoryAssignments);
            }));
    }

    TeardownProductsForSubinventory(siteSubinventoryObs: Observable<number | null>): Observable<{ teardowns: Product[], scrap: Product[], idScrap: Product[] }> {
        return combineLatest([this._currentProducts, siteSubinventoryObs]).pipe(
            filter(([summary, siteSubinventoryId]) => summary !== null && siteSubinventoryId !== null),
            map(([summary, siteSubinventoryId]) => {
                return {
                    teardowns: this._filterProductsForSubinventory(summary.products, siteSubinventoryId, summary.teardownAssignments),
                    scrap: this._filterProductsForSubinventory(summary.products, siteSubinventoryId, summary.scrapAssignments),
                    idScrap: this._filterProductsForSubinventory(summary.products, siteSubinventoryId, summary.identifiedScrapAssignments)
                };
            }));
    }

    ComponentBayTypesForSubinventory(siteSubinventoryObs: Observable<number | null>): Observable<BayType[]> {
        return combineLatest([this._currentProducts, siteSubinventoryObs]).pipe(
            filter(([summary, siteSubinventoryId]) => summary !== null && siteSubinventoryId !== null),
            map(([summary, siteSubinventoryId]) => {
                const assignments = summary.componentBayTypes.filter(a => a.siteSubinventoryId === siteSubinventoryId);

                return assignments.map(a => a.bayType);
            }));
    }

    SortActivityTypesForSubinventory(siteSubinventoryObs: Observable<number | null>): Observable<SortActivity[]> {
        return combineLatest([this._currentProducts, siteSubinventoryObs]).pipe(
            filter(([summary, siteSubinventoryId]) => summary !== null && siteSubinventoryId !== null),
            map(([summary, siteSubinventoryId]) => {
                const assignments = summary.sortActivityTypes.filter(a => a.siteSubinventoryId === siteSubinventoryId);

                return assignments.map(a => a.sortActivityType);
            }));
    }


}
